import React from 'react';
import { Box, TextField, Typography, type TextFieldProps } from '@mui/material';
import { AI_AGENT_UI } from './tokens';

type Props = Omit<TextFieldProps, 'variant' | 'size'> & {
  /** Stretch to fill form grid cell. */
  stretch?: boolean;
};

/**
 * Stacked-label text field for AI Agent slot-filling forms only.
 * Avoids MUI outline-notch + tenant CSS zoom clipping/ghosting labels.
 */
export function AgentTextField({
  label,
  stretch,
  fullWidth = true,
  sx,
  InputProps,
  inputProps,
  ...rest
}: Props) {
  const reactId = React.useId();
  const fieldId = rest.id ?? `agent-field-${reactId}`;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        minWidth: 0,
        width: stretch || fullWidth ? '100%' : undefined,
        flex: stretch ? '1 1 160px' : undefined,
        ...((sx as object) || {}),
      }}
    >
      {label != null && label !== '' && (
        <Typography
          component="label"
          htmlFor={fieldId}
          sx={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.01em',
            lineHeight: 1.2,
            color: AI_AGENT_UI.textSecondary,
            px: 0.25,
            userSelect: 'none',
          }}
        >
          {label}
        </Typography>
      )}
      <TextField
        {...rest}
        id={fieldId}
        size="small"
        variant="outlined"
        fullWidth={fullWidth}
        InputProps={InputProps}
        inputProps={inputProps}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: `${AI_AGENT_UI.radius.sm}px`,
            fontSize: AI_AGENT_UI.font,
            bgcolor: AI_AGENT_UI.surface,
            '& fieldset': {
              borderWidth: 1.5,
              borderColor: AI_AGENT_UI.borderStrong,
            },
            '&:hover fieldset': {
              borderColor: '#9ca3af',
            },
            '&.Mui-focused fieldset': {
              borderColor: AI_AGENT_UI.primary,
              borderWidth: 1.5,
            },
          },
          '& .MuiInputBase-input': {
            py: 1.05,
            px: 1.5,
            fontSize: AI_AGENT_UI.font,
            '&::placeholder': {
              color: AI_AGENT_UI.textSecondary,
              opacity: 0.72,
            },
          },
          '& .MuiFormHelperText-root': {
            mx: 0.25,
            mt: 0.5,
            fontSize: AI_AGENT_UI.fontSm,
          },
        }}
      />
    </Box>
  );
}

/** Shared row layout for AI Agent slot-filling forms. */
export const agentFormRowSx = {
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    sm: 'repeat(2, minmax(0, 1fr))',
  },
  gap: 1.25,
  alignItems: 'start',
  mb: 1.25,
  width: '100%',
} as const;

export const agentFormActionsSx = {
  display: 'flex',
  gap: 1,
  alignItems: 'center',
  flexWrap: 'wrap',
  gridColumn: { xs: 'auto', sm: '1 / -1' },
  pt: 0.25,
} as const;
